import { NgClass } from '@angular/common'
import { Component, computed, DestroyRef, inject, input, output, signal } from '@angular/core'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms'
import { AuthService } from '@app/modules/auth/services/auth.service'
import { WorkspaceRoleService } from '@app/modules/auth/services/workspace-role.service'
import { PersonaService } from '@app/v2/personas/services/persona.service'
import { ButtonSpinnerDirective } from '@app/v2/shared/button-loader.directive'
import { DropdownComponent } from '@app/v2/shared/components/dropdown/dropdown.component'
import { DropdownOption } from '@app/v2/shared/components/dropdown/types/dropdown-option.type'
import { BasicInputComponent } from '@app/v2/shared/components/inputs/basic-input/basic-input.component'
import { ModalComponent } from '@app/v2/shared/components/modal/modal.component'
import { DeleteModalComponent } from '@app/v2/shared/components/upload/components/delete-modal/delete-modal.component'
import { WizardService } from '@app/v2/shared/components/wizard/services/wizard.service'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { NormalizeEventNamePipe } from '@app/v2/shared/pipes/normalize-event-name.pipe'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AppService } from '@app/v2/shared/services/app.service'
import { ModalService } from '@app/v2/shared/services/modal.service'
import { finalize, take } from 'rxjs'
import { EventWizardComponent } from '../../event-wizard/event-wizard.component'
import { EventTimelineSyncService } from '../../services/event-timeline-sync.service'
import { EventService } from '../../services/event.service'
import { Event } from '../../types/event.type'
import { downloadEventIdentifiersFile } from '../../utils/event-identifiers-file.utils'
import { ArchivedItemsComponent } from '../archived-items/archived-items.component'

@Component({
  selector: 'app-event-header',
  standalone: true,
  imports: [NgClass, DropdownComponent, DeleteModalComponent, ModalComponent, BasicInputComponent, ReactiveFormsModule, ButtonSpinnerDirective, NormalizeEventNamePipe],
  providers: [NormalizeEventNamePipe],
  templateUrl: './event-header.component.html',
  styleUrl: './event-header.component.scss'
})
export class EventHeaderComponent {
  public readonly deleteHeaderIcon = `<svg width="15" height="16" viewBox="0 0 15 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13.8519 3.36842H10.5926V1.68421C10.5926 1.23753 10.4209 0.809145 10.1153 0.493294C9.80967 0.177443 9.39517 0 8.96296 0H5.7037C5.2715 0 4.857 0.177443 4.55138 0.493294C4.24577 0.809145 4.07407 1.23753 4.07407 1.68421V3.36842H0.814815C0.598712 3.36842 0.391461 3.45714 0.238654 3.61507C0.0858463 3.77299 0 3.98719 0 4.21053C0 4.43387 0.0858463 4.64806 0.238654 4.80598C0.391461 4.96391 0.598712 5.05263 0.814815 5.05263H1.62963V14.3158C1.62963 14.7625 1.80132 15.1909 2.10694 15.5067C2.41255 15.8226 2.82705 16 3.25926 16H11.4074C11.8396 16 12.2541 15.8226 12.5597 15.5067C12.8653 15.1909 13.037 14.7625 13.037 14.3158V5.05263H13.8519C14.068 5.05263 14.2752 4.96391 14.428 4.80598C14.5808 4.64806 14.6667 4.43387 14.6667 4.21053C14.6667 3.98719 14.5808 3.77299 14.428 3.61507C14.2752 3.45714 14.068 3.36842 13.8519 3.36842ZM5.7037 1.68421H8.96296V3.36842H5.7037V1.68421ZM6.51852 12.6316C6.51852 12.8549 6.43267 13.0691 6.27986 13.227C6.12706 13.385 5.91981 13.4737 5.7037 13.4737C5.4876 13.4737 5.28035 13.385 5.12754 13.227C4.97474 13.0691 4.88889 12.8549 4.88889 12.6316V6.73684C4.88889 6.5135 4.97474 6.29931 5.12754 6.14138C5.28035 5.98346 5.4876 5.89474 5.7037 5.89474C5.91981 5.89474 6.12706 5.98346 6.27986 6.14138C6.43267 6.29931 6.51852 6.5135 6.51852 6.73684V12.6316ZM9.77778 12.6316C9.77778 12.8549 9.69193 13.0691 9.53912 13.227C9.38632 13.385 9.17907 13.4737 8.96296 13.4737C8.74686 13.4737 8.53961 13.385 8.3868 13.227C8.23399 13.0691 8.14815 12.8549 8.14815 12.6316V6.73684C8.14815 6.5135 8.23399 6.29931 8.3868 6.14138C8.53961 5.98346 8.74686 5.89474 8.96296 5.89474C9.17907 5.89474 9.38632 5.98346 9.53912 6.14138C9.69193 6.29931 9.77778 6.5135 9.77778 6.73684V12.6316Z" fill="#111928"/></svg>`

  public readonly event = input.required<Event>()
  public readonly archived = input(false)
  public readonly canEdit = input(true)
  public readonly canCreate = input(true)
  public readonly cloning = input(false)
  public readonly cloningMessage = input('')
  public readonly dropdownOptions = input<DropdownOption[]>([])

  public readonly modalStateChanged = output<boolean>()
  public readonly cloneConfirmed = output<void>()
  public readonly archiveConfirmed = output<void>()
  public readonly restoreConfirmed = output<void>()
  public readonly deletePermanentlySucceeded = output<Event>()

  public readonly deletingPermanently = signal(false)
  public readonly permanentDeleteConfirmationControl = new FormControl<string>('', {
    nonNullable: true,
    validators: [
      Validators.required,
      (control) => {
        const value = String(control.value ?? '')
        if (!value) return null
        const expected = `Delete ${this._normalizeEventNamePipe.transform(this.event()?.name ?? '')}`
        return value === expected ? null : { serverError: 'Confirmation text does not match. Please type the exact event name to proceed.' }
      }
    ]
  })

  private readonly _normalizeEventNamePipe = inject(NormalizeEventNamePipe)
  private readonly _modalService = inject(ModalService)
  private readonly _wizardService = inject(WizardService)
  private readonly _appService = inject(AppService)
  private readonly _authService = inject(AuthService)
  private readonly _eventService = inject(EventService)
  private readonly _eventTimelineSyncService = inject(EventTimelineSyncService)
  private readonly _alertService = inject(AlertService)
  private readonly _workspaceRoleService = inject(WorkspaceRoleService)
  private readonly _userRole = toSignal(this._workspaceRoleService.getUserRole(), { initialValue: null })
  private readonly _personaService = inject(PersonaService)
  private readonly _destroyRef = inject(DestroyRef)

  public readonly cloneConfirmMessage = computed(() => {
    const name = this.event().name ?? ''
    return `Are you sure you want to clone <strong>${name}</strong> event?<br>Cloned events will have all the configuration copied and the dates adjusted.`
  })

  public readonly archiveConfirmMessage = computed(() => {
    const name = this.event().name ?? ''
    return `Are you sure you want to archive <strong>${name}</strong> event?`
  })

  public readonly restoreConfirmMessage = computed(() => {
    const name = this.event().name ?? ''
    return `Are you sure you want to restore <strong>${name}</strong> event?<br>Restored events will be available in your events timeline.`
  })

  public readonly visibleEventMenuOptions = computed((): DropdownOption[] => this.dropdownOptions())
  public readonly canAccessEventMenu = computed((): boolean => {
    this._userRole()
    return this._workspaceRoleService.canShareInTimeline()
  })
  public readonly eventMenuDropdownHeight = computed((): number => this.visibleEventMenuOptions().length * 49)

  public readonly cloneModalId = computed(() => `clone-event-modal-${this.event().id}`)
  public readonly archiveModalId = computed(() => `archive-event-modal-${this.event().id}`)
  public readonly restoreModalId = computed(() => `restore-event-modal-${this.event().id}`)
  public readonly deletePermanentlyModalId = computed(() => `delete-event-permanently-modal-${this.event().id}`)

  public isModalOpened(id: string): boolean {
    return this._modalService.isModalOpen(id)
  }

  /**
   * Navigate to the edit event wizard
   */
  public navigateToEdit(): void {
    if (this.archived() || !this.canEdit()) return
    const eventId = this.event().id
    const result = this._wizardService.open({
      component: EventWizardComponent,
      componentInputs: {
        eventId,
        industrySectors: this._appService.industrySectors
      },
      headerConfig: {
        breadcrumbs: [this.event().name]
      }
    })
    result.onClose.pipe(take(1)).subscribe(() => {
      this._eventService.requestTimelineRefresh({ type: 'event', eventId })
    })
  }

  /**
   * Handle the event menu option click
   * @param option {DropdownOption} - The dropdown option
   */
  public onEventMenuOption(option: DropdownOption): void {
    if (option.key === 'event-identifiers') this.downloadEventIdentifiers()
    else if (option.key === 'clone') this.openCloneModal()
    else if (option.key === 'archived-items') this.openArchivedItems()
    else if (option.key === 'archive') this.openArchiveModal()
    else if (option.key === 'restore') this.openRestoreModal()
    else if (option.key === 'delete-permanently') this.openDeletePermanentlyModal()
  }

  /**
   * Downloads a text file with the workspace, event, and persona identifiers.
   */
  public downloadEventIdentifiers(): void {
    const workspace = this._authService.currentWorkspace
    if (!workspace?.name || workspace.id == null) {
      this._alertService.alert(Alert.ERROR, 'Could not download identifiers. Missing workspace details.')
      return
    }

    downloadEventIdentifiersFile({
      event: this.event(),
      workspace: {
        name: workspace.name,
        id: workspace.id,
        slug: workspace.slug ?? ''
      },
      personaService: this._personaService,
      appService: this._appService
    })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        error: () => this._alertService.alert(Alert.ERROR, 'Failed to download event identifiers.')
      })
  }

  /**
   * Opens the archived items wizard for the current event.
   */
  public openArchivedItems(): void {
    this._wizardService.open({
      component: ArchivedItemsComponent,
      componentInputs: {
        eventId: this.event().id,
        organizationId: this.event().workspace_id,
        eventName: this.event().name,
        eventTimelineSync: this._eventTimelineSyncService
      },
      headerConfig: {
        breadcrumbs: [this.event().name, 'Archive']
      }
    })
  }

  /**
   * Open the clone modal
   */
  public openCloneModal(): void {
    this._modalService.toggleModal(this.cloneModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Close the clone modal
   */
  public closeCloneModal(): void {
    if (this._modalService.isModalOpen(this.cloneModalId())) {
      this._modalService.toggleModal(this.cloneModalId())
    }
    this.modalStateChanged.emit(false)
  }

  /**
   * Open the archive modal
   */
  public openArchiveModal(): void {
    this._modalService.toggleModal(this.archiveModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Close the archive modal
   */
  public closeArchiveModal(): void {
    if (this._modalService.isModalOpen(this.archiveModalId())) {
      this._modalService.toggleModal(this.archiveModalId())
    }
    this.modalStateChanged.emit(false)
  }

  /**
   * Open the restore confirmation modal
   */
  public openRestoreModal(): void {
    this._modalService.toggleModal(this.restoreModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Close the restore confirmation modal
   */
  public closeRestoreModal(): void {
    if (this._modalService.isModalOpen(this.restoreModalId())) {
      this._modalService.toggleModal(this.restoreModalId())
    }
    this.modalStateChanged.emit(false)
  }

  /**
   * Open the delete permanently confirmation modal
   */
  public openDeletePermanentlyModal(): void {
    this._modalService.toggleModal(this.deletePermanentlyModalId())
    this.modalStateChanged.emit(true)
  }

  /**
   * Close the delete permanently confirmation modal
   */
  public closeDeletePermanentlyModal(): void {
    if (this._modalService.isModalOpen(this.deletePermanentlyModalId())) {
      this._modalService.toggleModal(this.deletePermanentlyModalId())
    }
    this.deletingPermanently.set(false)
    this.permanentDeleteConfirmationControl.reset('', { emitEvent: false })
    this.permanentDeleteConfirmationControl.markAsUntouched()
    this.permanentDeleteConfirmationControl.markAsPristine()
    this.modalStateChanged.emit(false)
  }

  /**
   * Handle the clone confirm click
   */
  public onCloneConfirmClick(): void {
    this.cloneConfirmed.emit()
  }

  /**
   * Handle the archive confirm click
   */
  public onArchiveConfirmClick(): void {
    this.archiveConfirmed.emit()
  }

  /**
   * Handle the restore confirm click
   */
  public onRestoreConfirmClick(): void {
    this.restoreConfirmed.emit()
  }

  /**
   * Handle the delete permanently confirm click
   */
  public onDeletePermanentlyConfirmClick(): void {
    this.permanentDeleteConfirmationControl.markAsTouched()
    this.permanentDeleteConfirmationControl.updateValueAndValidity()
    if (this.permanentDeleteConfirmationControl.invalid) return

    if (this.deletingPermanently()) return
    if (!this._workspaceRoleService.canDeleteArchivedEvents()) return
    this.deletingPermanently.set(true)

    const confirmationText = this._normalizeEventNamePipe.transform(this.permanentDeleteConfirmationControl.value)
    this._eventService
      .deleteEventPermanently(this._authService.currentWorkspace.id, this.event().id, confirmationText)
      .pipe(
        finalize(() => {
          this.deletingPermanently.set(false)
          this.closeDeletePermanentlyModal()
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe({
        next: () => {
          this._alertService.alert(Alert.SUCCESS, 'Event has been deleted permanently.')
          this.deletePermanentlySucceeded.emit(this.event())
        },
        error: (error: { error?: { message?: string }; message?: string }) => {
          this._alertService.alert(Alert.ERROR, error?.error?.message || error?.message || 'Failed to delete event permanently')
        }
      })
  }
}
